import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Maximum width — Tailwind class, e.g. "max-w-md" / "max-w-lg". */
  size?: string;
  /** If true the close-on-backdrop-click + Escape handlers are
   * suppressed. Use for irreversible flows (show-once secret) where
   * the user must explicitly acknowledge. */
  modal?: boolean;
}

/**
 * Minimal dialog — Tailwind + native DOM, no Radix dependency.
 * Locks body scroll while open, closes on Escape + backdrop click
 * (unless ``modal=true``), and traps initial focus on the close
 * button. Designed for the Sesi FE-2 create/edit/disable/revoke
 * flows where we only need simple panel + form composition.
 */
export function Dialog({
  open,
  onClose,
  children,
  size = "max-w-md",
  modal = false,
}: DialogProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open || modal) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, modal, onClose]);

  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Portal to <body> so the overlay is never confined by an ancestor's
  // stacking context / transform / overflow — guarantees the backdrop
  // covers the full viewport (incl. the fixed sidebar) for every dialog.
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={modal ? undefined : onClose}
        aria-hidden="true"
      />
      <div
        className={cn(
          // Flex column so the body slot can scroll while header
          // and footer stay pinned. Capped at 85vh (not 100vh) so
          // there's visible breathing space above + below the
          // dialog — full-viewport dialogs feel oppressive.
          "relative flex max-h-[85vh] w-full flex-col overflow-hidden rounded-lg border border-ocean-100 bg-white shadow-lg",
          size
        )}
      >
        {!modal && (
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 rounded-md p-1 text-neutral-500 transition-colors hover:bg-ocean-100 hover:text-ocean-600"
            aria-label="Tutup dialog"
          >
            <X size={18} />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body
  );
}

interface DialogSlotProps {
  children: ReactNode;
  className?: string;
}

export function DialogHeader({ children, className }: DialogSlotProps) {
  return (
    <div
      className={cn("border-b border-ocean-100 px-5 py-4", className)}
    >
      {children}
    </div>
  );
}

export function DialogTitle({ children, className }: DialogSlotProps) {
  return (
    <h2 className={cn("pr-8 text-base font-medium text-ocean-900", className)}>
      {children}
    </h2>
  );
}

export function DialogDescription({ children, className }: DialogSlotProps) {
  return (
    <p className={cn("mt-1 text-sm text-neutral-600", className)}>{children}</p>
  );
}

export function DialogBody({ children, className }: DialogSlotProps) {
  // ``max-h`` here is the belt-and-braces guard: when the body lives
  // inside a wrapper that breaks the panel's flex chain (e.g. a
  // ``<form>`` between the panel and the body), the body still
  // bounds its own height + scrolls instead of overflowing the
  // viewport. Math mirrors the panel cap (85vh) minus ~9rem for
  // header + footer + outer padding.
  return (
    <div
      className={cn(
        "max-h-[calc(85vh-9rem)] space-y-4 overflow-y-auto px-5 py-4",
        className
      )}
    >
      {children}
    </div>
  );
}

export function DialogFooter({ children, className }: DialogSlotProps) {
  return (
    <div
      className={cn(
        "flex items-center justify-end gap-2 border-t border-ocean-100 bg-ocean-50/40 px-5 py-3",
        className
      )}
    >
      {children}
    </div>
  );
}
